import { Chess } from 'chess.js';
function uciMove(chess, uci) {
    try {
        return chess.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] });
    }
    catch {
        return null;
    }
}
/** The position the learner first sees: the opponent's move already played. */
export function startPuzzle(p) {
    const chess = new Chess(p.fen);
    const first = uciMove(chess, p.moves[0]);
    if (!first)
        throw new Error(`puzzle ${p.id}: first move ${p.moves[0]} is illegal`);
    return { fen: chess.fen(), ply: 1, color: chess.turn(), lastMove: [first.from, first.to], solved: false };
}
/** Judge the learner's move, and play the reply when it was right. */
export function playMove(p, s, uci) {
    const chess = new Chess(s.fen);
    const move = uciMove(chess, uci);
    if (!move)
        return { kind: 'illegal' };
    const played = move.lan;
    const expected = p.moves[s.ply];
    const mates = chess.isCheckmate();
    const alsoRight = s.ply === 1 && !!p.accept?.includes(played);
    if (played !== expected && !alsoRight && !mates)
        return { kind: 'wrong', played: move.san, expected: sanOf(s.fen, expected) };
    const last = s.ply + 1 >= p.moves.length || (alsoRight && played !== expected);
    if (last || mates) {
        return {
            kind: 'solved',
            played: move.san,
            state: { fen: chess.fen(), ply: s.ply + 1, color: s.color, lastMove: [move.from, move.to], solved: true },
        };
    }
    const reply = uciMove(chess, p.moves[s.ply + 1]);
    if (!reply)
        throw new Error(`puzzle ${p.id}: reply ${p.moves[s.ply + 1]} is illegal`);
    return {
        kind: 'correct',
        played: move.san,
        reply: reply.san,
        state: { fen: chess.fen(), ply: s.ply + 2, color: s.color, lastMove: [reply.from, reply.to], solved: false },
    };
}
/** The learner's next move in the stored line, as `[from, to]` — the hint. */
export function hintOf(p, s) {
    const m = p.moves[s.ply];
    return m ? [m.slice(0, 2), m.slice(2, 4)] : null;
}
/** The whole remaining line in SAN, from this state — the shown solution. */
export function solutionFrom(p, s) {
    const chess = new Chess(s.fen);
    const out = [];
    for (const m of p.moves.slice(s.ply)) {
        const mv = uciMove(chess, m);
        if (!mv)
            break;
        out.push(mv.san);
    }
    return out;
}
function sanOf(fen, uci) {
    return uciMove(new Chess(fen), uci)?.san ?? uci;
}
export function piecesOf(fen) {
    const out = [];
    for (const row of new Chess(fen).board()) {
        for (const sq of row)
            if (sq)
                out.push({ square: sq.square, color: sq.color, type: sq.type });
    }
    return out;
}
/** Squares the piece on `from` may legally move to. */
export function targetsOf(fen, from) {
    return new Chess(fen).moves({ square: from, verbose: true }).map((m) => m.to);
}
/** Whether `from → to` is a pawn reaching the last rank. */
export function isPromotion(fen, from, to) {
    return new Chess(fen)
        .moves({ square: from, verbose: true })
        .some((m) => m.to === to && !!m.promotion);
}
/** The square of the king in check, if the side to move is in check. */
export function checkedKing(fen) {
    const chess = new Chess(fen);
    if (!chess.inCheck())
        return null;
    const turn = chess.turn();
    return piecesOf(fen).find((p) => p.type === 'k' && p.color === turn)?.square ?? null;
}
export const sideToMove = (fen) => (fen.split(' ')[1] === 'b' ? 'b' : 'w');
//# sourceMappingURL=puzzle.js.map